import { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Snackbar, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { walletApi } from '../../../services/api/walletApi';
import '../accountTheme.css';
import './WithdrawScreen.css';

const AMOUNTS = ['200', '500', '1000', '2000', '5000'];
const MIN = 200;

function WithdrawScreen({ user, onBack, onOpenAddBank, bankAccounts = [], selectedBankId, onWalletUpdate }) {
  const [amountInput, setAmountInput] = useState('500');
  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const balance = user?.walletBalance ?? 0;
  const amount = Number(amountInput || 0);
  const selectedBank =
    bankAccounts.find((a) => a.id === selectedBankId) || bankAccounts[0] || null;
  const hasBankCard = bankAccounts.length > 0;
  const valid = Number.isFinite(amount) && amount >= MIN && amount <= balance && hasBankCard;

  useEffect(() => {
    if (!selectedBankId && bankAccounts[0]?.id) {
      // parent should set selected bank; no-op here
    }
  }, [bankAccounts, selectedBankId]);

  const handleWithdraw = async () => {
    if (!hasBankCard) {
      onOpenAddBank?.();
      return;
    }
    if (!valid || submitting) {
      if (!valid && hasBankCard) {
        setToast(amount > balance ? 'Insufficient balance' : `Minimum withdrawal is ₹${MIN}`);
      }
      return;
    }

    setSubmitting(true);
    setToast('');
    try {
      const result = await walletApi.submitWithdraw(selectedBank.id, amount);
      setToast(result.message || `₹${amount.toLocaleString('en-IN')} deducted. Withdrawal submitted.`);
      onWalletUpdate?.();
    } catch (err) {
      setToast(err.message || 'Could not submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="bz-page withdraw-page">
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} />
      <Box className="bz-topbar">
        <IconButton size="small" onClick={onBack} className="bz-back-btn">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="bz-topbar__title">Withdraw</Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box className="bz-scroll">
        <Box className="bz-balance-hero withdraw-balance-hero">
          <Typography className="bz-balance-label">Available to withdraw</Typography>
          <Typography className="bz-balance-value" style={{ fontSize: '1.6rem' }}>
            ₹{balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </Typography>
        </Box>

        <Typography className="bz-section-title">Bank account</Typography>
        <button type="button" className="bz-card withdraw-bank-card" onClick={onOpenAddBank}>
          <AccountBalanceRoundedIcon sx={{ fontSize: 32, color: '#60a5fa' }} />
          <Box sx={{ flex: 1, textAlign: 'left' }}>
            <Typography className="withdraw-bank-title">
              {selectedBank ? selectedBank.bankName : 'Add bank account'}
            </Typography>
            <Typography className="withdraw-bank-sub">
              {selectedBank
                ? `A/C ****${selectedBank.accountNumber.slice(-4)} · ${selectedBank.ifscCode}`
                : 'Required for withdrawals'}
            </Typography>
          </Box>
          <span className="withdraw-bank-arrow">→</span>
        </button>

        {!hasBankCard && (
          <Typography className="withdraw-hint" style={{ textAlign: 'left', marginTop: 12 }}>
            Link a bank account before withdrawing
          </Typography>
        )}

        <Typography className="bz-section-title">Withdrawal amount</Typography>
        <Box className="bz-card">
          <Box className="bz-amount-grid">
            {AMOUNTS.filter((a) => Number(a) <= balance || balance === 0).map((a) => (
              <button
                key={a}
                type="button"
                className={`bz-amount-chip ${amountInput === a ? 'selected' : ''}`}
                onClick={() => setAmountInput(a)}
              >
                ₹{Number(a).toLocaleString('en-IN')}
              </button>
            ))}
          </Box>
          <Box className="bz-input-wrap">
            <Typography sx={{ color: '#60a5fa', fontWeight: 800, fontSize: '1.2rem' }}>₹</Typography>
            <input
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value.replace(/[^\d]/g, ''))}
              placeholder={`Min ₹${MIN}`}
              inputMode="numeric"
            />
          </Box>
          <Typography className="withdraw-hint">
            Min ₹{MIN} · Max ₹{balance.toLocaleString('en-IN')}
          </Typography>
        </Box>

        <Box className="bz-card withdraw-note">
          <Typography className="deposit-note-title">Terms and Conditions</Typography>
          <Typography className="withdraw-note-line">• Minimum withdrawal amount is ₹200</Typography>
          <Typography className="withdraw-note-line">• Maximum withdrawal requests per day: 3 times</Typography>
          <Typography className="withdraw-note-line">• Amount is deducted from wallet when you submit</Typography>
          <Typography className="withdraw-note-line">• Admin verifies your bank details and sends money within 24 hours</Typography>
          <Typography className="withdraw-note-line">• Ensure your bank details are correct before requesting</Typography>
        </Box>
      </Box>

      <Box className="bz-bottom-bar">
        <button
          type="button"
          className="bz-primary-btn withdraw-submit-btn"
          disabled={(!valid && hasBankCard) || submitting}
          onClick={handleWithdraw}
        >
          {submitting ? (
            <CircularProgress size={22} sx={{ color: '#fff' }} />
          ) : hasBankCard ? (
            `Withdraw ₹${valid ? amount.toLocaleString('en-IN') : '—'}`
          ) : (
            'Add bank account'
          )}
        </button>
      </Box>
    </Box>
  );
}

export default WithdrawScreen;
